import {Formik, Form, FormikValues} from "formik";
import React, {useEffect, useMemo, useState} from "react";
import toast from "react-hot-toast";
import {startCase} from "lodash";
import axiosInstant from "../../../../api";
import {getNotificationsSettings, getActivityKinds} from "../../../../api/endpoints";
import {Button} from "../../../../components/Button/Button";
import {FieldSwitch} from "../../../../components/fields/Switch/Switch";
import {useUser} from "../../../../hooks/useUser";
import {DotLottleLoader} from "../../../../components/Loader";
import type {SystemRoleKey} from "../../../../providers/roles";

// One kind string -> one Formik toggle name ("project created" ->
// "projectCreated"). Single helper so its consumers (defaults, saved
// opt-outs, render) can never drift from each other or from the inverse
// mapping in handleOnSubmit (which re-inserts spaces before capitals).
// NOTE: deliberately splits on spaces only — hyphenated kinds
// ("expert-opt-response") round-trip unchanged, which lodash camelCase
// would break.
const toToggleName = (kind: string): string =>
    kind
        .split(" ")
        .map((word, index) =>
            index === 0
                ? word.toLowerCase()
                : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join("");

// localStorage key (suffixed with the viewer's role, since the backend filters
// recipients per caller) holding the last-good /activity-log/kinds payload,
// used as a fallback when the fetch fails so the page keeps its role-scoped
// view across transient outages.
const KINDS_CACHE_KEY = "activityLogKindsCache";

// Backend recipient TOKEN (from ACTIVITY_REGISTRY.recipients, served by
// GET /activity-log/kinds) -> the SystemRoles keys whose users see (and can opt
// out of) that kind's toggle. This is the ONE place the frontend translates the
// registry's audience into UI roles (Option A, TDC-1387): the registry owns WHO
// gets notified; this map owns how each token renders. It mirrors the backend
// token switch in sendNotification (brandProjects.js) — most tokens are 1:1;
// 'brand' fans out to EVERY brand-side role (the backend resolves it via
// getUsersByBrand, which returns all of a brand's users — admins, members, and
// brand-sales alike, so all three must be able to mute it); 'pros' is
// the experts a call site names (PRO).
const RECIPIENT_TOKEN_TO_ROLES: Record<string, SystemRoleKey[]> = {
    admin: ["ADMIN"],
    "account manager admin": ["ACCOUNT_MANAGER_ADMIN"],
    "pro team admin": ["PRO_TEAM_ADMIN"],
    "post production admin": ["POST_PRODUCTION_ADMIN"],
    sales: ["SALES_TEAM"],
    brand: ["BRAND_ADMIN", "BRAND_MEMBER", "BRAND_TEAM_SALES"],
    pros: ["PRO"],
    // The people a comment or brief note @mentions: internal staff, and the
    // project's brand users on comments. Brand callers get no 'tagee' on the
    // brief note kind (the API withholds it), so they see only the comment one.
    tagee: [
        "ADMIN",
        "ACCOUNT_MANAGER_ADMIN",
        "PRO_TEAM_ADMIN",
        "SALES_TEAM",
        "POST_PRODUCTION_ADMIN",
        "BRAND_ADMIN",
        "BRAND_MEMBER",
        "BRAND_TEAM_SALES",
    ],
    internal: [
        "ADMIN",
        "ACCOUNT_MANAGER_ADMIN",
        "PRO_TEAM_ADMIN",
        "SALES_TEAM",
        "POST_PRODUCTION_ADMIN",
    ],
    everyone: [
        "ADMIN",
        "ACCOUNT_MANAGER_ADMIN",
        "PRO_TEAM_ADMIN",
        "SALES_TEAM",
        "POST_PRODUCTION_ADMIN",
        "BRAND_ADMIN",
        "BRAND_MEMBER",
        "BRAND_TEAM_SALES",
    ],
};

// One entry of GET /activity-log/kinds — the backend ACTIVITY_REGISTRY
// (desirelist-api .../lib/controllers/activityRegistry.js) serialized per
// caller. That registry is the ONLY source of this page: which kinds exist,
// whether each one notifies (severity/keepInbox), who receives it (recipients)
// and the copy under its toggle (description). Nothing here duplicates it.
type KindMeta = {
    severity: string;
    keepInbox: boolean;
    recipients: string[];
    description: string | null;
};

type NotificationKind = {
    kind: string;
    description: string;
};

// Which SystemRoles see a kind's toggle: the registry's `recipients` tokens
// mapped through RECIPIENT_TOKEN_TO_ROLES.
const rolesForKind = (
    entry: NotificationKind,
    kindMeta: Record<string, KindMeta>,
): SystemRoleKey[] => {
    const meta = kindMeta[entry.kind];
    if (!meta) return [];
    return (meta.recipients || []).flatMap(
        (token) => RECIPIENT_TOKEN_TO_ROLES[token] || [],
    );
};

// A kind renders a toggle iff it actually notifies (major, or minor+keepInbox).
// Log-only kinds (minor, non-keepInbox) never reach the inbox/email/websocket,
// so there is nothing for the user to turn off.
const notifies = (meta: KindMeta): boolean =>
    !(meta.severity === "minor" && !meta.keepInbox);

export default function BrandNotificationSetting() {
    // The user's saved opt-outs (kind strings) — null until the fetch settles.
    const [optOutKinds, setOptOutKinds] = useState<string[] | null>(null);
    const [kindMeta, setKindMeta] = useState<Record<string, KindMeta>>({});
    const [kindsLoading, setKindsLoading] = useState(true);
    // Set when /activity-log/kinds failed AND no cached copy existed: the page
    // then has no registry to render from and must NOT show a form (see below).
    const [kindsError, setKindsError] = useState(false);
    // Bumped by the retry button to re-run the kinds fetch.
    const [kindsAttempt, setKindsAttempt] = useState(0);
    const {role} = useUser();

    useEffect(() => {
        const controller = new AbortController();
        const fetchNotificationSettings = async () => {
            try {
                const response = await axiosInstant.get(
                    getNotificationsSettings(),
                    {signal: controller.signal},
                );
                const {data} = response;
                setOptOutKinds(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                // Fail open to defaults (everything on) instead of spinning
                // forever — and say so, because saving in this state would
                // overwrite any opt-outs the fetch failed to load.
                toast.error(
                    "Could not load your saved notification settings, so every notification is shown as on. Save now and you would overwrite your existing choices.",
                );
                setOptOutKinds([]);
            }
        };
        fetchNotificationSettings();
    }, []);

    // Fetch the kind registry from the backend — the ONLY source of this
    // page's toggles. On failure, fall back to the last successfully fetched
    // copy for this role; if there is none either, surface an error instead
    // of a form: saving from a page that doesn't know every kind would replace
    // the user's whole opt-out list with a partial one (createSetting is a
    // full replace), silently re-enabling everything they had turned off.
    useEffect(() => {
        const controller = new AbortController();
        // Per-role cache slot: the backend filters recipients per caller, so a
        // shared browser must never fall back to another role's taxonomy.
        const cacheKey = `${KINDS_CACHE_KEY}:${role || "any"}`;
        setKindsLoading(true);
        setKindsError(false);
        (async () => {
            try {
                const {data} = await axiosInstant.get(getActivityKinds(), {
                    signal: controller.signal,
                });
                const payload = (data && data.data) || data || {};
                const kinds = payload.kinds || {};
                if (Object.keys(kinds).length === 0) {
                    throw new Error("Empty notification registry");
                }
                setKindMeta(kinds);
                try {
                    localStorage.setItem(cacheKey, JSON.stringify(kinds));
                } catch {
                    // Best-effort cache (quota / private mode) — ignore.
                }
            } catch (error) {
                if (controller.signal.aborted) return;
                let cached: Record<string, KindMeta> | null = null;
                try {
                    const raw = localStorage.getItem(cacheKey);
                    cached = raw ? JSON.parse(raw) : null;
                } catch {
                    cached = null;
                }
                if (cached && Object.keys(cached).length > 0) {
                    setKindMeta(cached);
                } else {
                    console.error(error);
                    setKindsError(true);
                }
            } finally {
                if (!controller.signal.aborted) setKindsLoading(false);
            }
        })();
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kindsAttempt]);

    // Every kind that renders a toggle: the notifying registry kinds, in
    // registry order, with the registry's own copy.
    const allKinds = useMemo<NotificationKind[]>(() => {
        const fromRegistry = Object.keys(kindMeta)
            .filter((kind) => notifies(kindMeta[kind]))
            .map((kind) => ({
                kind,
                description:
                    kindMeta[kind].description ||
                    `Notify when a "${kind}" event occurs`,
            }));
        return fromRegistry;
    }, [kindMeta]);

    // Formik initial values: every renderable kind defaults ON, then the saved
    // opt-outs flip theirs OFF. Computed only once BOTH fetches settle.
    const initialValues = useMemo<Record<string, boolean> | null>(() => {
        if (optOutKinds === null || kindsLoading) return null;
        const values: Record<string, boolean> = {};
        allKinds.forEach((entry) => {
            values[toToggleName(entry.kind)] = true;
        });
        optOutKinds.forEach((kind) => {
            const toggleName = toToggleName(kind);
            if (Object.prototype.hasOwnProperty.call(values, toggleName)) {
                values[toggleName] = false;
            }
        });
        return values;
    }, [optOutKinds, kindsLoading, allKinds]);

    const handleOnSubmit = async (values: FormikValues) => {
        const mutedKinds = Object.keys(values)
            .filter((key) => !values[key])
            // Convert camelCase back to space-separated format
            .map((key) =>
                key
                    .replace(/([A-Z])/g, " $1")
                    .toLowerCase()
                    .trim(),
            );
        try {
            await axiosInstant.post(getNotificationsSettings(), {
                listKind: mutedKinds,
            });
            setOptOutKinds(mutedKinds);
            toast.success(
                mutedKinds.length === 0
                    ? "Notification settings saved. You will receive every notification."
                    : `Notification settings saved. ${mutedKinds.length} notification${
                        mutedKinds.length === 1 ? " is" : "s are"
                    } turned off.`,
            );
        } catch (error) {
            console.error(error);
            toast.error(
                "Could not save your notification settings. Please try again.",
            );
        }
    };

    // No registry (fetch failed, nothing cached): no form. See the fetch effect
    // for why rendering a partial form here would be worse than an error.
    if (!kindsLoading && kindsError) {
        return (
            <div>
                <h3 className="text-dark text-xl sm:text-3xl mb-6 font-bold uppercase font-[CircularStd]">
                    Notifications
                </h3>
                <p className="text-gray2 text-sm sm:text-base mb-6">
                    Couldn&apos;t load the notification settings. Please try again.
                </p>
                <Button
                    type="button"
                    className="!w-full sm:!w-auto"
                    onClick={() => setKindsAttempt((n) => n + 1)}
                >
                    Retry
                </Button>
            </div>
        );
    }

    // Don't render until BOTH fetches settle — the toggle list is derived from
    // the registry, and initialValues stays null until the settings fetch
    // settles too.
    if (kindsLoading || !initialValues) {
        return <DotLottleLoader fill={false} />;
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleOnSubmit}
        >
            <Form>
                <h3 className="text-text-1 text-xl sm:text-3xl mb-6 font-bold uppercase font-[CircularStd]">
                    Notifications
                </h3>

                <div className="flex gap-4 sm:gap-5 flex-col">
                    {role &&
                        allKinds
                            // Only the kinds this role actually receives: the
                            // audience is the registry's `recipients` (mapped
                            // through RECIPIENT_TOKEN_TO_ROLES), so a user
                            // never sees a toggle for a notification they
                            // would never get.
                            .filter((notification) =>
                                rolesForKind(notification, kindMeta).includes(
                                    role,
                                ),
                            )
                            .map((notification, index) => {
                                const toggleName = toToggleName(
                                    notification.kind,
                                );
                                return (
                                    <div
                                        key={index}
                                        className="flex items-start gap-3 justify-between py-3 border-b border-border-token last:border-b-0"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="font-bold text-sm sm:text-base">
                                                {startCase(notification.kind)}
                                            </div>
                                            <div className="text-text-2 text-xs sm:text-sm leading-relaxed mt-0.5">
                                                {notification.description
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    notification.description.slice(
                                                        1,
                                                    )}
                                                .
                                            </div>
                                        </div>
                                        <div className="shrink-0 mt-0.5">
                                            <FieldSwitch name={toggleName} />
                                        </div>
                                    </div>
                                );
                            })}
                </div>

                <div className="mt-8 sm:mt-12">
                    <Button type="submit" className="!w-full sm:!w-auto">Save changes</Button>
                </div>
            </Form>
        </Formik>
    );
}
